<script lang="ts">
    import * as Sentry from "@sentry/svelte";
    import { onDestroy, onMount } from "svelte";
    import { computePosition, flip, shift, offset, autoUpdate } from "@floating-ui/dom";
    import type { Readable } from "svelte/store";
    import { AskPositionMessage_AskType } from "@workadventure/messages";
    import teleport from "../../images/teleport.svg";
    import businessCard from "../../images/business-cards.svg";
    import type { ChatUser } from "../../Connection/ChatConnection";
    import { gameManager } from "../../../Phaser/Game/GameManager";
    import { localUserStore } from "../../../Connection/LocalUserStore";
    import { mercadologicaPostItStore } from "../../../Stores/MercadologicaPostItStore";
    import { scriptUtils } from "../../../Api/ScriptUtils";
    import { requestVisitCardsStore } from "../../../Stores/GameStore";
    import { wokaMenuStore } from "../../../Stores/WokaMenuStore";
    import { LL } from "../../../../i18n/i18n-svelte";
    import { showReportScreenStore } from "../../../Stores/ShowReportScreenStore";
    import { analyticsClient } from "../../../Administration/AnalyticsClient";
    import type { UserProviderMerger } from "../../UserProviderMerger/UserProviderMerger";
    import { IconForbid, IconDots, IconCamera, IconMapPin, IconUserPlus } from "@wa-icons";

    interface Props {
        user: ChatUser;
    }

    let { user }: Props = $props();

    let popoversElement: HTMLDivElement | undefined = $state();

    let buttonElement: HTMLButtonElement;

    let chatMenuActive = $state(false);

    const MERCADOLOGICA_NUDGE_VARIABLE = "__mercadologica_nudge";
    const NUDGE_COOLDOWN_MS = 10_000;

    let nudgeOnCooldown = $state(false);
    let nudgeCooldownTimer: number | undefined;

    let usersByRoomStore:
        | Readable<Map<string | undefined, { roomName: string | undefined; users: ChatUser[] }>>
        | undefined = $state(undefined);

    let cleanup: undefined | (() => void);

    $effect(() => {
        if (popoversElement && buttonElement) {
            cleanup = autoUpdate(buttonElement, popoversElement, repositionIfOverflowing);
            return () => {
                cleanup?.();
                cleanup = undefined;
            };
        }
        return undefined;
    });
    let usersByRoomMap = $derived(usersByRoomStore && $usersByRoomStore ? $usersByRoomStore : new Map());
    // Flatten usersByRoomMap into a list of users with playUri from their room
    let usersWithRoomPlayUri = $derived(
        (() => {
            const usersList: (ChatUser & { playUri: string })[] = [];
            for (const [playUri, roomData] of usersByRoomMap.entries()) {
                for (const user of roomData.users) {
                    usersList.push({
                        ...user,
                        playUri: playUri ?? user.playUri ?? "",
                    });
                }
            }
            return usersList;
        })(),
    );
    let userToLocate = $derived(usersWithRoomPlayUri.find((u) => u.uuid === user.uuid));

    const { connection, roomUrl } = gameManager.getCurrentGameScene();

    let isInTheSameMap = $derived(user.playUri === roomUrl);

    const iAmAdmin = connection?.hasTag("admin");

    const goTo = (type: string, playUri: string, uuid: string) => {
        analyticsClient.goToUser();

        if (type === "room") {
            scriptUtils.goToPage(`${playUri}#moveToUser=${uuid}`);
        } else if (type === "user") {
            if (user.uuid && connection && user.playUri) connection.emitAskPosition(user.uuid, user.playUri);
        }
    };

    function repositionIfOverflowing() {
        if (!buttonElement || !popoversElement) return;
        const currentPopoversElement = popoversElement;
        computePosition(buttonElement, currentPopoversElement, {
            middleware: [offset(6), flip(), shift({ padding: 5 })],
        })
            .then(({ x, y }) => {
                Object.assign(currentPopoversElement.style, {
                    left: `${x}px`,
                    top: `${y}px`,
                });
            })
            .catch((error) => {
                console.error("Failed to compute popover position : ", error);
            });
    }

    const closeChatUserMenu = () => {
        chatMenuActive = false;
    };

    const toggleChatUSerMenu = () => {
        chatMenuActive = !chatMenuActive;
    };

    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
        if (event.target && popoversElement && !popoversElement.contains(event.target as Node)) {
            closeChatUserMenu();
        }
    };

    onMount(() => {
        gameManager
            .getCurrentGameScene()
            .userProviderMerger.then((merger: UserProviderMerger) => {
                usersByRoomStore = merger.usersByRoomStore;
            })
            .catch((error) => {
                console.error("Failed to get users by room store : ", error);
            });
    });

    onDestroy(() => {
        if (cleanup) cleanup();

        if (nudgeCooldownTimer !== undefined) {
            window.clearTimeout(nudgeCooldownTimer);
        }
    });

    function sendNudge(): void {
        if (!user.uuid || nudgeOnCooldown || !isInTheSameMap) {
            return;
        }

        const scene = gameManager.getCurrentGameScene();
        const sender = localUserStore.getLocalUser();

        scene.getPlayerVariablesManager().setVariableFromApp(
            MERCADOLOGICA_NUDGE_VARIABLE,
            {
                targetUuid: user.uuid,
                targetSpaceUserId: user.spaceUserId,
                senderUuid: sender?.uuid ?? "",
                senderName: localUserStore.getName() ?? "Alguém",
                nonce:
                    typeof crypto.randomUUID === "function"
                        ? crypto.randomUUID()
                        : `${Date.now()}-${Math.random()}`,
                sentAt: Date.now(),
            },
            {
                persist: false,
                public: true,
                scope: "room",
                force: true,
            },
        );

        nudgeOnCooldown = true;

        try {
            scene.playSound("msn-wizz", 0.25);
        } catch (error) {
            console.error("Failed to play nudge confirmation sound:", error);
            Sentry.captureException(error);
        }

        nudgeCooldownTimer = window.setTimeout(() => {
            nudgeOnCooldown = false;
            nudgeCooldownTimer = undefined;
        }, NUDGE_COOLDOWN_MS);

        closeChatUserMenu();
    }

    const showBusinessCard = (visitCardUrl: string | undefined) => {
        analyticsClient.showBusinessCard();

        // If woka menu is open, close it
        if ($wokaMenuStore) {
            wokaMenuStore.clear();
        }

        if (visitCardUrl) {
            if ($requestVisitCardsStore == visitCardUrl) {
                requestVisitCardsStore.set(null);
                closeChatUserMenu();
                return;
            }
            requestVisitCardsStore.set(visitCardUrl);
        }
        closeChatUserMenu();
    };

    function locateUser() {
        if (userToLocate == undefined || userToLocate.uuid == undefined) return;

        // Check if visit card url is the same as the current visit card url
        if ($requestVisitCardsStore != undefined) {
            requestVisitCardsStore.set(null);
        }

        // Track the open woka menu action
        analyticsClient.openWokaMenu();

        const currentScerne = gameManager.getCurrentGameScene();

        // Il user is in view port and represented by remote player, use it to activate the woka menu
        const remotePlayerData = currentScerne.getRemotePlayersRepository().getPlayerByUuid(userToLocate.uuid);
        if (remotePlayerData != undefined) {
            // Get the actual RemotePlayer sprite from MapPlayersByKey using userId
            const remotePlayer = currentScerne.MapPlayersByKey.get(remotePlayerData.userId);
            if (remotePlayer != undefined) {
                remotePlayer.activate();
                closeChatUserMenu();
                return;
            }
        }

        // If the user isn't in the view port, emit the ask position message to the server
        currentScerne.connection?.emitAskPosition(
            userToLocate.uuid ?? "",
            userToLocate.playUri ?? "",
            AskPositionMessage_AskType.LOCATE,
        );
        closeChatUserMenu();
    }
</script>

<svelte:window onclick={handleClickOutside} ontouchstart={handleClickOutside} />
<div class="wa-dropdown">
    <button
        class="m-0 p-2 flex items-center rounded-md hover:bg-white/10 bg-transparent !text-white"
        bind:this={buttonElement}
        onclick={(event) => {
            event.stopPropagation();
            toggleChatUSerMenu();
        }}
    >
        <IconDots font-size="16" />
    </button>
    <!-- onmouseleave={closeChatUserMenu} -->
    {#if chatMenuActive}
        <div
            bind:this={popoversElement}
            class="wa-dropdown-menu z-10 mr-1 fixed bg-contrast/80 backdrop-blur-md rounded-md p-1"
        >
            {#if isInTheSameMap}
                <!-- svelte-ignore a11y_click_events_have_key_events -->
                <!-- svelte-ignore a11y_no_static_element_interactions -->
                <span
                    class="walk-to wa-dropdown-item text-nowrap flex gap-2 items-center hover:bg-white/10 m-0 p-2 w-full text-sm rounded"
                    onclick={(event) => {
                        event.stopPropagation();
                        goTo("user", user.playUri ?? "", user.uuid ?? "");
                        closeChatUserMenu();
                    }}
                >
                    <IconCamera class="w-4" />
                    {$LL.chat.userList.TalkTo()}</span
                >
                <!-- svelte-ignore a11y_click_events_have_key_events -->
                <!-- svelte-ignore a11y_no_static_element_interactions -->
                <span
                    class={`follow wa-dropdown-item text-nowrap flex gap-2 items-center hover:bg-white/10 m-0 p-2 w-full text-sm rounded ${
                        userToLocate == undefined ? "opacity-50 cursor-not-allowed pointer-events-none" : ""
                    }`}
                    onclick={(event) => {
                        event.stopPropagation();
                        locateUser();
                    }}
                >
                    <IconMapPin class="w-4" />
                    {$LL.chat.userList.follow()}
                </span>
                <!-- MERCADOLOGICA: botão de cutucão -->
                <!-- svelte-ignore a11y_click_events_have_key_events -->
                <!-- svelte-ignore a11y_no_static_element_interactions -->
                <span
                    class={`nudge wa-dropdown-item text-nowrap flex gap-2 items-center hover:bg-white/10 m-0 p-2 w-full text-sm rounded ${
                        nudgeOnCooldown ? "opacity-50 cursor-not-allowed pointer-events-none" : ""
                    }`}
                    title={nudgeOnCooldown
                        ? "Aguarde 10 segundos para cutucar esta pessoa novamente"
                        : "Chamar a atenção desta pessoa"}
                    onclick={(event) => {
                        event.stopPropagation();
                        sendNudge();
                    }}
                >
                    <span class="w-4 text-center" aria-hidden="true">👋</span>
                    {nudgeOnCooldown ? "Aguarde para cutucar" : "Cutucar"}
                </span>

                <!-- MERCADOLOGICA: deixar recado persistente -->
                <!-- svelte-ignore a11y_click_events_have_key_events -->
                <!-- svelte-ignore a11y_no_static_element_interactions -->
                <span
                    class="post-it wa-dropdown-item text-nowrap flex gap-2 items-center hover:bg-white/10 m-0 p-2 w-full text-sm rounded"
                    onclick={(event) => {
                        event.stopPropagation();

                        if (user.uuid) {
                            mercadologicaPostItStore.openCompose({
                                uuid: user.uuid,
                                name: user.username ?? user.uuid,
                            });
                        }

                        closeChatUserMenu();
                    }}
                >
                    <span class="w-4 text-center" aria-hidden="true">📝</span>
                    Deixar recado
                </span>

                <!-- svelte-ignore a11y_click_events_have_key_events -->
                <!-- svelte-ignore a11y_no_static_element_interactions -->
                <span
                    class="invite wa-dropdown-item text-nowrap flex gap-2 items-center hover:bg-white/10 m-0 p-2 w-full text-sm rounded"
                    data-testid="user-list-invite"
                    onclick={(event) => {
                        event.stopPropagation();
                        if (user.uuid) {
                            const scene = gameManager.getCurrentGameScene();
                            const sent = scene.inviteManager?.requestMeetingInvitation(user.uuid);
                            if (sent) {
                                try {
                                    scene.playSound("meeting-in", 0.15);
                                } catch (error) {
                                    console.error("Failed to play sound: ", error);
                                    Sentry.captureException(error);
                                }
                            }
                        }
                        closeChatUserMenu();
                    }}
                >
                    <IconUserPlus class="w-4" />
                    {$LL.chat.userList.invite()}
                </span>
            {:else if user.playUri}
                <!-- svelte-ignore a11y_click_events_have_key_events -->
                <!-- svelte-ignore a11y_no_static_element_interactions -->
                <span
                    class="teleport wa-dropdown-item text-nowrap flex gap-2 items-center hover:bg-white/10 m-0 p-2 w-full text-sm rounded"
                    onclick={(event) => {
                        event.stopPropagation();
                        goTo("room", user.playUri ?? "", user.uuid ?? "");
                        closeChatUserMenu();
                    }}
                    ><img
                        class="noselect"
                        src={teleport}
                        alt={$LL.chat.userList.teleport()}
                        height="13"
                        width="13"
                        draggable="false"
                    />
                    {$LL.chat.userList.teleport()}</span
                >
                <!-- svelte-ignore a11y_click_events_have_key_events -->
                <!-- svelte-ignore a11y_no_static_element_interactions -->
                <span
                    class="invite wa-dropdown-item text-nowrap flex gap-2 items-center hover:bg-white/10 m-0 p-2 w-full text-sm rounded"
                    data-testid="user-list-invite"
                    onclick={(event) => {
                        event.stopPropagation();
                        if (user.uuid) {
                            const scene = gameManager.getCurrentGameScene();
                            const sent = scene.inviteManager?.requestMeetingInvitation(user.uuid);
                            if (sent) {
                                try {
                                    scene.playSound("meeting-in", 0.15);
                                } catch (error) {
                                    console.error("Failed to play sound: ", error);
                                    Sentry.captureException(error);
                                }
                            }
                        }
                        closeChatUserMenu();
                    }}
                >
                    <IconUserPlus class="w-4" />
                    {$LL.chat.userList.invite()}
                </span>
            {/if}
            <!-- svelte-ignore a11y_click_events_have_key_events -->
            <!-- svelte-ignore a11y_no_static_element_interactions -->
            {#if user.visitCardUrl}
                <span
                    class="businessCard wa-dropdown-item text-nowrap flex gap-2 items-center hover:bg-white/10 m-0 p-2 w-full text-sm rounded"
                    onclick={(event) => {
                        event.stopPropagation();
                        showBusinessCard(user.visitCardUrl);
                    }}
                    ><img
                        class="noselect"
                        src={businessCard}
                        alt={$LL.chat.userList.businessCard()}
                        height="13"
                        width="13"
                        draggable="false"
                    />
                    {$LL.chat.userList.businessCard()}</span
                >
            {/if}

            {#if iAmAdmin}
                <!-- svelte-ignore a11y_click_events_have_key_events -->
                <!-- svelte-ignore a11y_no_static_element_interactions -->
                <span
                    class="ban wa-dropdown-item text-pop-red text-nowrap flex gap-2 items-center hover:bg-white/10 m-0 p-2 w-full text-sm rounded"
                    onclick={(event) => {
                        event.stopPropagation();
                        if (user.username && user.uuid) {
                            showReportScreenStore.set({ userUuid: user.uuid, userName: user.username });
                        }
                    }}><IconForbid font-size="13" /> {$LL.chat.ban.title()}</span
                >
            {/if}
        </div>
    {/if}
</div>
